import { Injectable } from '@angular/core';
import {
    DomSanitizer,
    SafeHtml,
    SafeResourceUrl,
} from '@angular/platform-browser';

import { lastValueFrom } from 'rxjs';

import { ContentService } from '../content/content.service';

export interface VideoOptions {
    autoplay?: boolean;
    autopause?: boolean;
    background?: boolean;
    controls?: boolean;
    loop?: boolean;
    height?: string;
    width?: string;
    maxheight?: string;
    maxwidth?: string;
    muted?: boolean;
    responsive?: boolean;
}

export interface VideoData {
    type: string;
    url: SafeResourceUrl | SafeHtml;
    options: any;
}

@Injectable()
export class VideoService {
    private thumbnail: string;
    private videoDataCache = new Map<string, VideoData>();
    private inFlightRequests = new Map<string, Promise<VideoData>>();

    constructor(
        private contentService: ContentService,
        private domSanitizer: DomSanitizer,
    ) {}

    async getVideoData(id: string, options?: VideoOptions): Promise<any> {
        return await this.getSafeVideoUrlFromInput(id, options);
    }

    getVideoThumbnail(): string {
        return this.thumbnail;
    }

    private getVideoCacheKey(id: string, options: VideoOptions): string {
        const entries = Object.entries(options || {}).sort(([a], [b]) =>
            a.localeCompare(b),
        );
        return `${id}|${JSON.stringify(entries)}`;
    }

    private getSafeValue(
        type: string,
        url: string,
    ): SafeHtml | SafeResourceUrl {
        const safeUrl =
            type === 'resource'
                ? this.domSanitizer.bypassSecurityTrustResourceUrl(url)
                : this.domSanitizer.bypassSecurityTrustHtml(url);
        return !!safeUrl['changingThisBreaksApplicationSecurity']
            ? safeUrl
            : '';
    }

    private setOptionDefaults(options: VideoOptions): VideoOptions {
        if (!options) {
            options = {} as VideoOptions;
        }
        const getProperty = (
            options: VideoOptions,
            propertyName: string,
            defaultValue: string | boolean,
        ): VideoOptions => {
            options[propertyName] = options?.hasOwnProperty(propertyName)
                ? options[propertyName]
                : defaultValue;
            return options;
        };

        options = getProperty(options, 'autoplay', true);
        options = getProperty(options, 'autopause', false);
        options = getProperty(options, 'controls', true);
        options = getProperty(options, 'dnt', true);
        if (options.background) {
            options = getProperty(options, 'muted', true);
            options = getProperty(options, 'loop', true);
            options = getProperty(options, 'false', true);
        }

        return options;
    }

    private async getSafeVideoUrlFromInput(
        id: string,
        options?: VideoOptions,
    ): Promise<VideoData> {
        const isUrl = /^(https?:)?\/\//i.test(id);
        const isId = /^\d+$/g.test(id);
        if (!id || (!isId && !isUrl)) {
            return null;
        }

        options = this.setOptionDefaults(options);

        let videoUrl: SafeHtml | SafeResourceUrl;
        let type: string;

        const videoId = id.toString();
        const cacheKey = this.getVideoCacheKey(videoId, options);

        const cachedVideoData = this.videoDataCache.get(cacheKey);
        if (cachedVideoData) {
            return cachedVideoData;
        }

        const inFlightRequest = this.inFlightRequests.get(cacheKey);
        if (inFlightRequest) {
            return inFlightRequest;
        }

        if (isUrl) {
            type = 'resource';
            videoUrl = this.getSafeValue(type, id);
            const result = { type, url: videoUrl, options };
            this.videoDataCache.set(cacheKey, result);
            return result;
        }

        const requestPromise = (async () => {
            try {
                const video = await lastValueFrom(
                    this.contentService.getVimeoVideo(videoId, options),
                );

                type = 'html';
                videoUrl = this.getSafeValue(type, video.html);

                this.thumbnail = video['thumbnail_url'];

                const result: VideoData = {
                    type,
                    url: videoUrl,
                    options,
                };

                this.videoDataCache.set(cacheKey, result);
                return result;
            } catch (e) {
                console.error('Vimeo error:', e);
                return null as any;
            } finally {
                this.inFlightRequests.delete(cacheKey);
            }
        })();

        this.inFlightRequests.set(cacheKey, requestPromise);
        return requestPromise;
    }
}
